import {
  ImageGeneratorGenerateResult,
  ImageGeneratorHistoryRecord,
} from "../../app/api/imageGenerator";
import {
  isGenericSurfaceLabel,
  isNumericId,
} from "../../constants/imageGeneratorDictionaries";
import { ImageGeneratorResultItem } from "./ImageGeneratorDesignDetails";

export const IMAGE_GENERATOR_SESSION_PARAM = "session";

export const imageGeneratorSessionTitle = (
  record: ImageGeneratorHistoryRecord
) => {
  const details = record.details;
  if (record.outputType === "style" || details?.outputType === "Style") {
    return (
      [details?.productType, details?.styleType].filter(Boolean).join(" · ") ||
      "Style look"
    );
  }
  return details?.microTheme || "Pattern";
};

export const imageGeneratorRootId = (
  record: ImageGeneratorHistoryRecord,
  byId: Map<number, ImageGeneratorHistoryRecord>
) => {
  let current = record;
  const seen = new Set<number>();
  while (current.sourceGenerationId && !seen.has(current.id)) {
    seen.add(current.id);
    const parent = byId.get(current.sourceGenerationId);
    if (!parent) {
      break;
    }
    current = parent;
  }
  return current.id;
};

export const imageGeneratorSessionChain = (
  items: ImageGeneratorHistoryRecord[],
  sessionId: number
) => {
  const byId = new Map(items.map(item => [item.id, item]));
  return items
    .filter(item => imageGeneratorRootId(item, byId) === sessionId)
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
};

export const imageGeneratorRootListStatus = (
  root: ImageGeneratorHistoryRecord,
  items: ImageGeneratorHistoryRecord[]
) => {
  const chain = imageGeneratorSessionChain(items, root.id);
  if (chain.some(item => item.status === "generating")) {
    return "generating" as const;
  }
  return root.status;
};

const normalizePreferenceStem = (stem: string) =>
  stem
    .trim()
    .toLowerCase()
    .replace(/\.(png|jpe?g|webp|gif|svg)$/i, "");

const isHttpUrl = (value?: string | null) =>
  /^https?:\/\//i.test((value || "").trim());

const denimPreferenceApiPath = (stem: string): string => {
  const normalized = normalizePreferenceStem(stem);
  if (normalized.startsWith("material-")) {
    return `image-generator/denim-assets/materials/${normalized.replace(
      /^material-/,
      ""
    )}`;
  }
  if (normalized.startsWith("wash-")) {
    return `image-generator/denim-assets/washes/${normalized.replace(
      /^wash-/,
      ""
    )}`;
  }
  return "";
};

const isReportAssetStem = (stem: string) => /^\d+$/.test(stem.trim());

/** Empty or digits only — not a slide/trend name. */
const isBareTrendId = (value?: string | null) => {
  const trimmed = String(value || "").trim();
  return !trimmed || isNumericId(trimmed);
};

/** A slide/trend title, not an id and not the word "Surface". */
const isRealSurfaceTrendName = (value?: string | null) => {
  const trimmed = String(value || "").trim();
  if (!trimmed || isBareTrendId(trimmed) || isGenericSurfaceLabel(trimmed)) {
    return false;
  }
  return true;
};

export type ImageGeneratorSurfaceCatalogLabel = {
  name?: string | null;
  reportTitle?: string | null;
};

/**
 * Name for the Surface row when microTheme is missing, the numeric id,
 * or the generic word "Surface". Does not replace microTheme
 * (preference hover keeps reading that field).
 * Catalog slide `.name` is the selected surface trend; `reportTitle` is
 * used only when that name is still generic.
 */
const surfaceNameFromRecord = (
  surfaceId: string,
  microTheme: string | undefined,
  patternTrend: string | null | undefined,
  catalog?: ImageGeneratorSurfaceCatalogLabel
) => {
  if (!surfaceId || isRealSurfaceTrendName(microTheme)) {
    return undefined;
  }
  const fromReference = String(patternTrend || "").trim();
  if (isRealSurfaceTrendName(fromReference)) {
    return fromReference;
  }
  const fromSlide = String(catalog?.name || "").trim();
  if (isRealSurfaceTrendName(fromSlide)) {
    return fromSlide;
  }
  const fromReport = String(catalog?.reportTitle || "").trim();
  if (isRealSurfaceTrendName(fromReport)) {
    return fromReport;
  }
  return undefined;
};

const resolvePreferenceUrl = (
  stem: string,
  existingUrl: string | undefined,
  assetUrlByStem?: Record<string, string>,
  referenceUrl?: string
) => {
  if (!stem && !existingUrl && !referenceUrl) {
    return "";
  }

  const normalized = normalizePreferenceStem(stem || "");
  const denimPath = denimPreferenceApiPath(stem || "");

  // Denim material/wash tiles never use report /assets/:id — always denim-assets.
  if (denimPath) {
    if (assetUrlByStem?.[stem]) {
      return assetUrlByStem[stem];
    }
    if (assetUrlByStem?.[normalized]) {
      return assetUrlByStem[normalized];
    }
    if (assetUrlByStem) {
      const match = Object.entries(assetUrlByStem).find(([key]) => {
        const keyNorm = normalizePreferenceStem(key);
        return (
          keyNorm === normalized ||
          normalized.endsWith(keyNorm) ||
          keyNorm.endsWith(normalized)
        );
      });
      if (match) {
        return match[1];
      }
    }
    return denimPath;
  }

  // Prefer live catalog / CDN matches over stale API proxy paths.
  if (assetUrlByStem?.[stem]) {
    return assetUrlByStem[stem];
  }
  if (assetUrlByStem?.[normalized]) {
    return assetUrlByStem[normalized];
  }
  if (assetUrlByStem) {
    const match = Object.entries(assetUrlByStem).find(([key]) => {
      const keyNorm = normalizePreferenceStem(key);
      return (
        keyNorm === normalized ||
        normalized.endsWith(keyNorm) ||
        keyNorm.endsWith(normalized)
      );
    });
    if (match) {
      return match[1];
    }
  }

  if (isHttpUrl(existingUrl)) {
    return existingUrl!.trim();
  }
  if (isHttpUrl(referenceUrl)) {
    return referenceUrl!.trim();
  }
  if (existingUrl?.trim().startsWith("image-generator/")) {
    return existingUrl.trim();
  }
  if (referenceUrl?.trim().startsWith("image-generator/")) {
    return referenceUrl.trim();
  }
  if (existingUrl?.trim()) {
    return existingUrl.trim();
  }
  if (!stem) {
    return "";
  }
  if (isHttpUrl(stem)) {
    return stem.trim();
  }
  // Numeric report-asset stems always have an authenticated preview path.
  if (isReportAssetStem(stem)) {
    return `image-generator/assets/${encodeURIComponent(stem.trim())}`;
  }
  if (referenceUrl?.trim() && !referenceUrl.includes("/")) {
    // relativeKey that is only a filename — not a usable API path
    return "";
  }
  return "";
};

export const mapImageGeneratorHistoryRecord = (
  record: ImageGeneratorHistoryRecord | ImageGeneratorGenerateResult,
  fallbackDetails?: ImageGeneratorResultItem["details"],
  assetUrlByStem?: Record<string, string>,
  surfaceCatalogById?: Record<string, ImageGeneratorSurfaceCatalogLabel>
): ImageGeneratorResultItem | null => {
  const details = record.details || fallbackDetails;
  if (!details) {
    return null;
  }

  const payload = record.requestPayload;
  const referenceStems = (record.references?.assets || [])
    .filter(asset => asset.attached !== false)
    .map(asset => asset.stem)
    .filter(Boolean);
  const payloadStems = (payload?.selectedAssetStems || []).filter(Boolean);
  const detailPrefs = details.preferences || [];
  const denimStems = [
    payload?.denimMaterialId
      ? `material-${payload.denimMaterialId}.png`
      : details.denimMaterialId
        ? `material-${details.denimMaterialId}.png`
        : "",
    payload?.denimWashId
      ? `wash-${payload.denimWashId}.png`
      : details.denimWashId
        ? `wash-${details.denimWashId}.png`
        : "",
  ].filter(Boolean);
  const stemOrder =
    detailPrefs.length > 0
      ? detailPrefs.map(item => item.stem).filter(Boolean)
      : payloadStems.length > 0
        ? payloadStems
        : referenceStems.length > 0
          ? referenceStems
          : denimStems;

  const preferenceByStem = new Map(
    detailPrefs
      .filter(item => item.stem)
      .map(item => [item.stem, item] as const)
  );

  const preferences = stemOrder.map(stem => {
    const existing = preferenceByStem.get(stem);
    const existingUrl =
      existing?.url ||
      (existing as { imageUrl?: string } | undefined)?.imageUrl ||
      "";
    const referenceAsset = (record.references?.assets || []).find(
      asset => asset.stem === stem
    ) as
      | {
          stem: string;
          attached?: boolean;
          relativeKey?: string | null;
          url?: string | null;
        }
      | undefined;
    const referenceUrl =
      referenceAsset?.url || referenceAsset?.relativeKey || "";
    const resolved = resolvePreferenceUrl(
      stem,
      existingUrl,
      assetUrlByStem,
      referenceUrl
    );
    return {
      stem,
      url: resolved || existingUrl,
    };
  });

  const surfaceId = String(details.surfaceId || "").trim();
  const surfaceName = surfaceNameFromRecord(
    surfaceId,
    details.microTheme,
    record.references?.patternTrend,
    surfaceCatalogById?.[surfaceId]
  );

  return {
    id: record.id != null ? String(record.id) : `${Date.now()}`,
    createdAt: record.createdAt || new Date().toISOString(),
    result: {
      outputType: record.outputType,
      status: record.status,
      season: record.season,
      prompt: record.prompt,
      model: record.model,
      references: record.references,
      imageUrl: record.imageUrl,
      backImageUrl: record.backImageUrl || null,
      review: record.review || {},
    },
    details: {
      ...details,
      ...(surfaceName ? { surfaceName } : {}),
      colourStory:
        details.colourStory || record.references?.colourStory || undefined,
      additionalPrompt:
        details.additionalPrompt ||
        (payload as { additionalPrompt?: string } | undefined)
          ?.additionalPrompt ||
        undefined,
      preferences,
    },
  };
};
